"use client";

import { useEffect, useMemo, useState } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from "@tanstack/react-table";
import { Icon } from "@/lib/icons/Icon";
import { FormStatus, Input } from "@/components/ui/Form";
import { Badge, type BadgeVariant } from "@/components/ui/Badge/Badge";
import {
  fetchUserSignDocuments,
  type UserSignDocument,
} from "@/lib/api/c-electronic-signs-user";
import styles from "./CElectronicSigns.module.css";

const PAGE_SIZE = 20;

/* Statusy dokumentu (parytet z define.php: M_ELECTRONIC_SIGNS_STATUS_*). */
const DOC_STATUS_NEW = 1;
const DOC_STATUS_END = 4;

/* Statusy podpisu usera (M_ELECTRONIC_SIGNS_AUTHORIZED_USERS_STATUS_*). */
const USER_STATUS_SIGNED = 3;

/* Etykiety enuma podpisu - paralelnie do sekcji [methods]/[statuses]/[user_statuses]
   w c_electronic_signs_pl.lang. Backend zwraca tylko numeryczne kody (spojnie z
   c_facture: "etykiety kolumn naleza do react-cms"). Fallback `String(n)` jest
   _glosny_ - gdy backend doda nowy kod a frontend o nim nie wie, widac liczbe
   zamiast pustego stringa (latwiej zauwazyc desync niz cichy fallback). */
const METHOD_LABELS: Record<number, string> = {
  1: "Skan dokumentu ręcznie podpisany",
  2: "ePUAP (przekierowanie do platformy ePUAP)",
  3: "Podpis kwalifikowany",
};
const DOC_STATUS_LABELS: Record<number, string> = {
  1: "Nowy",
  2: "Przekazany do podpisu",
  3: "W trakcie podpisu",
  4: "Zakończony",
};
const USER_STATUS_LABELS: Record<number, string> = {
  1: "Oczekuje na podpis",
  2: "W trakcie podpisywania",
  3: "Podpisano",
};

/**
 * Plugin dokumentow do podpisu usera (shell c_user_accounts) - REST/React.
 *
 * Akcja hash-routable (z `sharingContentForApi`): `your-documents` (lista dokumentow).
 * Parytet z legacy `C_ELECTRONIC_SIGNS_CONTROLLER::yourDocuments()` - dokumenty, do
 * ktorych user jest osoba upowazniona do podpisu.
 *
 * **Grid:** TanStack Table v8 (headless) - ten sam wzorzec co lista uzytkownikow /
 * faktur: caly bounded zbior pobrany raz, sort/filtr/paginacja client-side.
 *
 * **Podglad:** link `preview_href` (`/IGETFILE/{blob}`) generowany server-side
 * przez `C_ELECTRONIC_SIGNS_CONTROLLER::getUserDocumentPreviewUrl` - id dokumentu
 * w tamper-proof payloadzie, ownership + serwowanie inline w handlerze
 * `userDocumentPreviewFile`. Browser nawiguje bezposrednio (bez proxy z Bearer -
 * IGETFILE dziala na PHP session cookie, parytet z c_facture pdf_href).
 *
 * **Podpis:** flow WYCHODZACY - `sign_href` to absolutny URL do formularza podpisu
 * na backendzie Imperial (ePUAP / kwalifikowany / skan). Opuszcza react-cms.
 */

/** Wariant Badge dla statusu dokumentu wg id_status. */
function docStatusVariant(idStatus: number): BadgeVariant {
  if (idStatus === DOC_STATUS_NEW) return "neutral";
  if (idStatus === DOC_STATUS_END) return "primary";
  return "warning";
}

/** Wariant Badge dla statusu podpisu usera wg user_sign_status. */
function userStatusVariant(userSignStatus: number): BadgeVariant {
  if (userSignStatus === USER_STATUS_SIGNED) return "success";
  return "warning";
}

/** Y-m-d H:i:s -> Y-m-d (data bez czasu). Defensywnie zwraca wejscie gdy brak spacji. */
function formatDate(raw: string): string {
  if (!raw) return "";
  return raw.split(" ")[0] || raw;
}

export function CElectronicSignsView() {
  const [rows, setRows] = useState<UserSignDocument[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "add_date", desc: true }]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { documents, truncated: tr } = await fetchUserSignDocuments();
        if (cancelled) return;
        setRows(documents);
        setTruncated(tr);
        setLoading(false);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const columns = useMemo(() => {
    const col = createColumnHelper<UserSignDocument>();
    return [
      col.accessor("name", {
        id: "name",
        header: () => "Dokument",
        cell: (ctx) => <span className={styles.docName}>{ctx.getValue()}</span>,
      }),
      col.accessor("add_date", {
        id: "add_date",
        header: () => "Data dodania",
        cell: (ctx) => formatDate(ctx.getValue()),
      }),
      col.accessor((row) => METHOD_LABELS[row.method] ?? String(row.method), {
        id: "method",
        header: () => "Metoda podpisu",
        cell: (ctx) => <Badge>{ctx.getValue()}</Badge>,
      }),
      col.accessor((row) => DOC_STATUS_LABELS[row.id_status] ?? String(row.id_status), {
        id: "status",
        header: () => "Status dokumentu",
        cell: (ctx) => (
          <Badge variant={docStatusVariant(ctx.row.original.id_status)}>{ctx.getValue()}</Badge>
        ),
      }),
      col.accessor(
        (row) => USER_STATUS_LABELS[row.user_sign_status] ?? String(row.user_sign_status),
        {
          id: "user_sign_status",
          header: () => "Status podpisu",
          cell: (ctx) => (
            <Badge variant={userStatusVariant(ctx.row.original.user_sign_status)}>
              {ctx.getValue()}
            </Badge>
          ),
        },
      ),
      col.display({
        id: "action",
        header: () => <span className="sr-only">Akcje</span>,
        cell: (ctx) => {
          const r = ctx.row.original;
          return (
            <span className={styles.actions}>
              {r.preview_href && (
                <a
                  href={r.preview_href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.previewBtn}
                  title="Podgląd dokumentu"
                  aria-label={`Podgląd dokumentu ${r.name}`}
                >
                  <Icon name="file-text" className={styles.actionIcon} />
                  Podgląd
                </a>
              )}
              {r.signable && r.sign_href ? (
                <a
                  href={r.sign_href}
                  className={styles.signBtn}
                  title="Podpisz dokument"
                  aria-label={`Podpisz dokument ${r.name}`}
                >
                  <Icon name="signature" className={styles.actionIcon} />
                  Podpisz
                </a>
              ) : (
                <span className={styles.signMuted}>
                  {r.user_sign_status === USER_STATUS_SIGNED ? "Podpisano" : "—"}
                </span>
              )}
            </span>
          );
        },
      }),
    ];
  }, []);

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: PAGE_SIZE } },
  });

  const pageCount = table.getPageCount();
  const pageIndex = table.getState().pagination.pageIndex;

  return (
    <div className={styles.root}>
      <p className={styles.note}>Dokumenty przekazane do Twojego podpisu.</p>

      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <Input
            type="search"
            prefix={<Icon name="magnifying-glass" />}
            placeholder="Szukaj (nazwa, status)..."
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            aria-label="Szukaj dokumentów"
          />
        </div>
      </div>

      {loading && <FormStatus variant="info">Wczytywanie dokumentów...</FormStatus>}
      {error && !loading && <FormStatus variant="error">{error}</FormStatus>}

      {!loading && !error && rows.length === 0 && (
        <FormStatus variant="info">Brak dokumentów do wyświetlenia.</FormStatus>
      )}

      {!loading && !error && rows.length > 0 && (
        <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => {
                      const canSort = header.column.getCanSort();
                      const sorted = header.column.getIsSorted();
                      return (
                        <th
                          key={header.id}
                          className={canSort ? styles.thSortable : undefined}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                          aria-sort={
                            sorted === "asc"
                              ? "ascending"
                              : sorted === "desc"
                                ? "descending"
                                : undefined
                          }
                        >
                          <span className={styles.thInner}>
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            {canSort && (
                              <Icon
                                name={sorted === "desc" ? "caret-down" : "caret-up"}
                                className={sorted ? styles.sortIconActive : styles.sortIcon}
                              />
                            )}
                          </span>
                        </th>
                      );
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {truncated && (
            <p className={styles.truncatedHint}>
              Wyświetlono pierwsze 500 dokumentów. Zawęź wyszukiwanie aby znaleźć starsze
              pozycje.
            </p>
          )}

          {pageCount > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                <Icon name="caret-left" />
                Poprzednia
              </button>
              <span className={styles.pageInfo}>
                Strona {pageIndex + 1} z {pageCount}
              </span>
              <button
                type="button"
                className={styles.pageButton}
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Następna
                <Icon name="caret-right" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Czy plugin obsluguje dana akcje (registry w ExtensionActionView).
 * Tylko akcje hash-routable z `sharingContentForApi`.
 */
export function cElectronicSignsHandlesAction(actionId: string): boolean {
  return actionId === "your-documents";
}
